import { useTranslation } from 'react-i18next'
import { Mail, Phone, UserRound } from 'lucide-react'
import { Avatar, Card, CardBody, CardHeader, CardTitle } from '@ludus/ui'
import { contactDisplayName, type Contact } from '@ludus/core'
import type { ApiError } from '@/lib/api/contacts.ts'

interface ContactCardProps {
  /** True iff `lead.contactId !== null`. Drives the "no link" empty state. */
  hasContactRef: boolean
  contact: Contact | undefined
  isLoading: boolean
  error: ApiError | undefined
}

export function ContactCard({ hasContactRef, contact, isLoading, error }: ContactCardProps) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('pages.leads.detail.contact.title', { defaultValue: 'Contact' })}</CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-3">
        {!hasContactRef ? (
          <EmptyState
            icon={<UserRound className="h-4 w-4" aria-hidden="true" />}
            text={t('pages.leads.detail.contact.empty', {
              defaultValue: 'No contact linked to this lead',
            })}
          />
        ) : isLoading ? (
          <span className="text-xs text-muted">
            {t('pages.leads.detail.contact.loading', { defaultValue: 'Loading contact…' })}
          </span>
        ) : error ? (
          <EmptyState
            icon={<UserRound className="h-4 w-4" aria-hidden="true" />}
            text={
              error.kind === 'not_found'
                ? t('pages.leads.detail.contact.empty', {
                    defaultValue: 'No contact linked to this lead',
                  })
                : t('pages.leads.detail.contact.errorGeneric', {
                    defaultValue: 'Could not load contact',
                  })
            }
          />
        ) : contact ? (
          <Populated contact={contact} />
        ) : null}
      </CardBody>
    </Card>
  )
}

function Populated({ contact }: { contact: Contact }) {
  const { t } = useTranslation()
  const fullName = contactDisplayName(contact)
  return (
    <>
      <div className="flex items-center gap-3">
        <Avatar size="md" seed={contact.id} initials={initials(contact)} aria-label={fullName} />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold text-foreground">{fullName}</span>
        </div>
      </div>
      <dl className="flex flex-col gap-2 text-sm">
        <ContactRow
          icon={<Phone className="h-3.5 w-3.5" aria-hidden="true" />}
          label={t('pages.leads.detail.contact.phone', { defaultValue: 'Phone' })}
        >
          {contact.phone ? (
            <a href={`tel:${contact.phone}`} className="text-foreground hover:underline">
              {contact.phone}
            </a>
          ) : (
            <span className="text-muted">—</span>
          )}
        </ContactRow>
        <ContactRow
          icon={<Mail className="h-3.5 w-3.5" aria-hidden="true" />}
          label={t('pages.leads.detail.contact.email', { defaultValue: 'Email' })}
        >
          {contact.email ? (
            <a href={`mailto:${contact.email}`} className="text-foreground hover:underline">
              {contact.email}
            </a>
          ) : (
            <span className="text-muted">—</span>
          )}
        </ContactRow>
      </dl>
    </>
  )
}

function ContactRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 text-muted">{icon}</span>
      <div className="flex min-w-0 flex-col">
        <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted">
          {label}
        </dt>
        <dd className="truncate text-sm">{children}</dd>
      </div>
    </div>
  )
}

function EmptyState({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-2 text-xs text-muted">
      <span className="text-muted/70">{icon}</span>
      <span>{text}</span>
    </div>
  )
}

function initials(c: Contact): string {
  return [c.firstName, c.lastName]
    .map((s) => s.trim().charAt(0).toUpperCase())
    .filter(Boolean)
    .join('')
}
